/**
 * ORGANIZATION-level announcements (F3-C): list (pinned first) + composer
 * for whoever can manage the academy. Notification fan-out to the roster is
 * done by the server.
 *
 * D9: the embargo of scheduled ones is NO LONGER filtered here. This view
 * used to hide future `publish_at` ones while the stream had already
 * replicated them to every device — a business rule with no authority. Now
 * the server materializes publication (`announcement.is_published`) and the
 * stream filters by it: if a scheduled announcement reaches this client it's
 * because the user drafted it or manages the academy, and then they MUST SEE
 * it, marked as scheduled.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Megaphone, Pin } from "@fleet/icons";
import {
  createAnnouncementMutation,
  deleteAnnouncementMutation,
  restoreTrashedMutation,
} from "@space/shared";
import { useUndo } from "@/lib/undo";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { PageHeader } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { InfoHint } from "@/components/ui/info-hint";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "../../hooks/use-active-org";
import { useMyOrgRole } from "../../hooks/use-people-data";
import { useAnnouncementsByOrg } from "../../hooks/use-community-data";
// AUTONOMY: scope (campus/cohort) + publish scheduling.
import { useCampuses } from "../../hooks/use-academy-data";
import { useCohortsByOrg } from "../../hooks/use-cohort-data";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

/**
 * D9: still-FUTURE publish date, or `undefined` if already published. Only
 * returns something for whoever receives scheduled drafts (its author or
 * whoever manages the academy): the server no longer sends them to anyone else.
 */
const scheduledFor = (publishAt: string | null): Date | undefined => {
  const at = publishAt ? Date.parse(publishAt) : Number.NaN;
  return !Number.isNaN(at) && at > Date.now() ? new Date(at) : undefined;
};

export const AnnouncementsView: Component = () => {
  const { t, locale } = useI18n();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const activeOrg = useActiveOrg();
  const orgId = () => activeOrg.activeOrgId();
  const role = useMyOrgRole(orgId);
  const canPost = () => role.can("ManageAcademics");
  const list = useAnnouncementsByOrg(orgId);

  // Everything that arrives is shown: the embargo is enforced by the server (D9).
  const visible = createMemo(() => list.data ?? []);

  const [title, setTitle] = createSignal("");
  const [body, setBody] = createSignal("");
  const [pinned, setPinned] = createSignal(false);
  // AUTONOMY: announcement scope ("" = whole academy) + scheduling.
  const [scope, setScope] = createSignal("");
  const [publishAt, setPublishAt] = createSignal("");
  const campuses = useCampuses(orgId);
  const cohorts = useCohortsByOrg(orgId);

  const scopeOptions = () => [
    { value: "", label: t(msg`Whole academy`) },
    ...(campuses.data ?? []).map((c) => ({
      value: `campus:${c.id}`,
      label: `${t(msg`Campus`)}: ${c.name ?? c.id}`,
    })),
    ...(cohorts.data ?? []).map((c) => ({
      value: `cohort:${c.id}`,
      label: `${t(msg`Cohort`)}: ${c.name ?? c.id}`,
    })),
  ];

  const publish = () => {
    const db = drizzle();
    const oid = orgId();
    const uid = userId();
    if (!db || !oid || !uid || !title().trim()) {
      return;
    }
    const [scopeKind, scopeId] = scope() ? scope().split(":") : ["", ""];
    void (async () => {
      await createAnnouncementMutation(db, {
        organizationId: oid,
        campusId: scopeKind === "campus" ? scopeId : null,
        cohortId: scopeKind === "cohort" ? scopeId : null,
        authorId: uid,
        title: title().trim(),
        body: body().trim(),
        pinned: pinned(),
        publishAt: publishAt() ? new Date(publishAt()).toISOString() : null,
      });
      setTitle("");
      setBody("");
      setPinned(false);
      setScope("");
      setPublishAt("");
    })();
  };

  const { registerUndo } = useUndo();
  const remove = (id: string) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    // F4-A: soft-delete + "Undo" toast (also restorable from /trash).
    // `registerUndo` already covers the undo's own failure (`useUndo`); what's
    // missing here is the initial delete: `patchRowVerified` can throw and
    // this is fire-and-forget.
    void (async () => {
      try {
        await deleteAnnouncementMutation(db, id, orgId());
      } catch (err) {
        console.error("Error deleting announcement:", err);
        showToast(t(msg`Could not delete the announcement`), "error");
        return;
      }
      registerUndo(t(msg`Announcement moved to trash`), async () => {
        const d = drizzle();
        if (d) {
          await restoreTrashedMutation(d, "announcement", id, orgId());
        }
      });
    })();
  };

  return (
    <div class="mx-auto max-w-2xl space-y-6 p-4 md:p-6">
      <div class="flex items-start justify-between gap-2">
        <PageHeader
          title={t(msg`Announcements`)}
          icon={Megaphone}
          description={t(
            msg`Messages from your academy: whole-academy, per campus or per cohort. Scheduled announcements appear at their publish date.`,
          )}
        />
      </div>

      <Show when={canPost()}>
        <div
          class="space-y-2 rounded-2xl border border-border bg-surface p-4"
          data-testid="announcement-composer"
        >
          <input
            class={field}
            data-testid="announcement-title"
            value={title()}
            onInput={(e) => setTitle(e.currentTarget.value)}
            placeholder={t(msg`Announcement title`)}
          />
          <textarea
            class={field}
            rows={3}
            data-testid="announcement-body"
            value={body()}
            onInput={(e) => setBody(e.currentTarget.value)}
            placeholder={t(msg`Write to your whole academy…`)}
          />
          {/* AUTONOMY: scope + scheduling (publish_at already existed in the table). */}
          <div class="grid gap-2 sm:grid-cols-2">
            <div class="space-y-1">
              <span class="text-xs text-muted-foreground">{t(msg`Audience`)}</span>
              <Select
                aria-label={t(msg`Audience`)}
                value={scope()}
                onChange={setScope}
                options={scopeOptions()}
              />
            </div>
            <label class="space-y-1">
              <span class="text-xs text-muted-foreground">
                {t(msg`Schedule (optional)`)}{" "}
                <InfoHint>
                  {t(
                    msg`Leave empty to publish now. With a future date, the announcement stays hidden until then.`,
                  )}
                </InfoHint>
              </span>
              <input
                type="datetime-local"
                class={field}
                data-testid="announcement-publish-at"
                value={publishAt()}
                onInput={(e) => setPublishAt(e.currentTarget.value)}
              />
            </label>
          </div>
          <div class="flex items-center justify-between">
            <label class="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                data-testid="announcement-pin"
                checked={pinned()}
                onChange={(e) => setPinned(e.currentTarget.checked)}
              />
              {t(msg`Pin to top`)}
            </label>
            <button
              type="button"
              data-testid="announcement-publish"
              onClick={publish}
              disabled={!title().trim()}
              class="rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground disabled:opacity-50"
            >
              {t(msg`Publish`)}
            </button>
          </div>
        </div>
      </Show>

      <ul class="space-y-2" data-testid="announcements-list">
        <For
          each={visible()}
          fallback={
            <li class="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              {t(msg`No announcements yet.`)}
            </li>
          }
        >
          {(a) => (
            <li
              data-testid="announcement-item"
              class="rounded-2xl border border-border bg-surface p-4"
            >
              <div class="flex items-start gap-2">
                <Show when={a.pinned}>
                  <Pin class="mt-1 h-4 w-4 shrink-0 text-accent" />
                </Show>
                <div class="flex-1">
                  <h3 class="font-bold">{a.title}</h3>
                  {/* D9: if this is visible, it's a manager's scheduled draft. */}
                  <Show when={scheduledFor(a.publish_at)}>
                    {(when) => (
                      <p
                        data-testid="announcement-scheduled"
                        class="mt-1 text-xs font-bold text-accent"
                      >
                        {t(msg`Scheduled`)} · {when().toLocaleString(locale())}
                      </p>
                    )}
                  </Show>
                  <Show when={a.body}>
                    <p class="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{a.body}</p>
                  </Show>
                </div>
                <Show when={canPost()}>
                  <button
                    type="button"
                    data-testid="announcement-delete"
                    onClick={() => remove(a.id)}
                    class="text-xs text-muted-foreground hover:text-danger"
                  >
                    {t(msg`Delete`)}
                  </button>
                </Show>
              </div>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
};
